import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Mission } from '../../types';

interface TimelineViewProps {
  missionId: string;
  mission: Mission;
}

interface TimelineEntry {
  id: string;
  timestamp: Date;
  type: 'status' | 'comment' | 'audit';
  title: string;
  detail?: string;
  user?: string;
  icon: string;
}

const TimelineView: React.FC<TimelineViewProps> = ({ missionId, mission }) => {
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAudit = async () => {
      try {
        const response = await fetch(`/api/audit/mission/${missionId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('teaam_token')}` },
        });
        const data = await response.json();
        if (data.success) setAuditLogs(data.data || []);
      } catch (err) {
        console.error('Failed to fetch audit logs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAudit();
  }, [missionId]);

  // Build unified timeline
  const entries: TimelineEntry[] = [];

  // Add status timestamps
  const statusFields: { field: keyof Mission; status: string; icon: string }[] = [
    { field: 'timeReceived', status: 'Received', icon: '\u{1F4E5}' },
    { field: 'timeAssigned', status: 'Assigned', icon: '\u{1F464}' },
    { field: 'timeAircraftNotified', status: 'Aircraft Notified', icon: '\u{1F4DE}' },
    { field: 'timeAircraftLaunched', status: 'Aircraft Launched', icon: '\u{1F681}' },
    { field: 'timeEnrouteToScene', status: 'Enroute to Scene', icon: '\u{1F6EB}' },
    { field: 'timeOnScene', status: 'On Scene', icon: '\u{1F4CD}' },
    { field: 'timePatientContact', status: 'Patient Contact', icon: '\u{1F3E5}' },
    { field: 'timeTransporting', status: 'Transporting', icon: '\u{1F691}' },
    { field: 'timeAtDestination', status: 'At Destination', icon: '\u{1F3E8}' },
    { field: 'timeMissionComplete', status: 'Mission Complete', icon: '\u2705' },
  ];

  statusFields.forEach(({ field, status, icon }) => {
    const value = mission[field];
    if (value) {
      entries.push({
        id: `status-${field}`,
        timestamp: new Date(value as any),
        type: 'status',
        title: status,
        icon,
      });
    }
  });

  // Add comments
  mission.comments?.forEach(comment => {
    entries.push({
      id: `comment-${comment.id}`,
      timestamp: new Date(comment.timestamp),
      type: 'comment',
      title: 'Comment',
      detail: comment.commentText,
      user: comment.dispatcher?.fullName || comment.dispatcherInitials,
      icon: '\u{1F4AC}',
    });
  });

  // Add audit log entries (field changes)
  auditLogs.forEach(log => {
    if (log.actionType === 'UPDATE' && log.fieldName) {
      entries.push({
        id: `audit-${log.id}`,
        timestamp: new Date(log.changedAt),
        type: 'audit',
        title: `${log.fieldName} changed`,
        detail: log.oldValue ? `${log.oldValue} \u2192 ${log.newValue}` : `Set to ${log.newValue}`,
        user: log.user?.fullName || 'System',
        icon: '\u{1F4DD}',
      });
    }
  });

  // Sort by timestamp descending (newest first)
  entries.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  if (loading) {
    return <div className="text-center py-8 text-gray-500">Loading timeline...</div>;
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Mission Timeline</h3>
      {entries.length === 0 ? (
        <p className="text-gray-500 text-sm">No timeline entries yet.</p>
      ) : (
        <div className="relative">
          <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200" />
          <div className="space-y-4">
            {entries.map(entry => (
              <div key={entry.id} className="relative flex items-start gap-4 pl-2">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center text-sm z-10">
                  {entry.icon}
                </div>
                <div className="flex-1 min-w-0 pb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-gray-900">{entry.title}</span>
                    {entry.user && <span className="text-xs text-gray-500">by {entry.user}</span>}
                    <span className="text-xs text-gray-400 ml-auto">{format(entry.timestamp, 'HH:mm:ss dd/MM')}</span>
                  </div>
                  {entry.detail && (
                    <p className="text-sm text-gray-600 mt-0.5">{entry.detail}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TimelineView;
